<!-- Side panel component for displaying package details -->
<template>
	<div class="flex w-full flex-col gap-10">
		<!--Package Image-->
		<div
			class="w-full"
			v-if="imageUrl"
		>
			<div
				class="relative aspect-435/360 w-full overflow-hidden rounded-lg shadow-lg"
			>
				<div
					class="absolute inset-0 bg-cover bg-center bg-no-repeat"
					:style="`background-image: url(${imageUrl});`"
				></div>
			</div>
		</div>

		<!--Package Details Section-->
		<div class="w-full overflow-x-hidden">
			<div class="flex w-full flex-col gap-7">
				<div>
					<div class="flex items-center gap-2">
						<h2 class="text-secondary-900 font-serif text-5xl italic">
							{{ pkg.name }}
						</h2>
					</div>

					<div class="flex items-center gap-2 py-2">
						<div class="text-primary-700 text-[22px] font-semibold">
							{{ formatPrice(packagePriceTotal) }}
						</div>
						<ShieldCheckIcon class="text-accent-600 size-6" />
					</div>

					<p class="mt-2 text-base text-slate-500">
						{{ pkg.description }}
					</p>

					<InputQuantity
						v-for="plan in plans"
						:key="plan.id"
						class="my-4 w-full"
						:plan="plan"
						:product="pkg"
					/>
				</div>

				<div>
					<!-- Package Line Items -->
					<ul
						class="flex w-full flex-col gap-5 *:flex *:w-full *:items-center *:justify-between"
					>
						<li
							v-for="product in pkgLineItems"
							:key="product.id"
						>
							<div class="text-secondary-900 text-base font-medium">
								{{ product.lineItem?.name }}
							</div>
							<PriceDetailsDisplay
								:price-details="{
									price:
										product.fixedPrice ?? product.lineItem?.fixedPrice ?? 0,
									originalPrice:
										product.fixedPrice ?? product.lineItem?.fixedPrice ?? 0,
								}"
								:credit="
									product.lineItem && qualifiesForDiscount(product.lineItem)
								"
								size="xs"
							/>
						</li>
					</ul>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import PriceDetailsDisplay from '../merchandise/PriceDetailsDisplay.vue'
import type {
	PbLineItem,
	PbLineItemPackage,
	PbPackageProduct,
} from '@/apiTypes'
import InputQuantity from '@/components/merchandise/InputQuantity.vue'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { useMerchandise } from '@/mixins/merchandise'
import type { AssetStoryblok } from '@/sbTypes'
import { useAssetStore } from '@/stores/asset'
import { useSessionStore } from '@/stores/session'
import ShieldCheckIcon from '@heroicons/vue/24/solid/ShieldCheckIcon'
import { computedAsync } from '@vueuse/core'
import { computed, type PropType } from 'vue'

const props = defineProps({
	pkg: {
		type: Object as PropType<PbLineItemPackage>,
		required: true,
	},
})

const {
	formatPrice,
	qualifiesForDiscount,
	priceBook,
	addLineItemToPackageProduct,
	calculatePackageTotal,
} = useMerchandise()
const asset = useAssetStore()
const sessionStore = useSessionStore()

const plans = computed(() => sessionStore.activePlans || [])

const packagePriceTotal = computed(() => calculatePackageTotal(props.pkg.id))

/**
 * Computed list of package line items for the given package.
 *
 * Sorts package products from the current PriceBook.
 * For non-Essentials: returns all sorted relations with line items.
 * For Essentials: returns only real (non-display, included) relations for this package.
 * Note: This shows all products from the price book for the package, not items in the cart,
 * so full lineItem details must be attached manually (attachLineItems).
 */
const pkgLineItems = computed<(PbPackageProduct & { lineItem: PbLineItem })[]>(
	() => {
		const currentPriceBook = priceBook.value
		if (!currentPriceBook) return []

		const isEssentialsPackage =
			props.pkg.subcategory === PbSubcategory.PackageEssentials

		// Helper: attach the matching line item to each relation and drop any that do not resolve.
		const attachLineItems = (relations: PbPackageProduct[]) =>
			relations
				.map(addLineItemToPackageProduct)
				.filter(
					(r): r is PbPackageProduct & { lineItem: PbLineItem } => !!r.lineItem
				)

		// Always compute the package’s sorted relations once.
		const sortedRelationsForThisPackage =
			(currentPriceBook.getSortedPackageProducts(
				currentPriceBook.getProductsInPackage(props.pkg.id) ?? [],
				true
			) ?? []) as PbPackageProduct[]

		// Non-Essentials: just show the sorted relations.
		if (!isEssentialsPackage) {
			return attachLineItems(sortedRelationsForThisPackage)
		}

		// Essentials: show only the "real" (non-display) relations that belong to this package.
		// Use the actual relation rows scoped by package id.
		const realRelationsForThisPackage = (
			currentPriceBook.packageProducts as PbPackageProduct[]
		).filter(
			(rel) =>
				rel.refSpbLinePackage === props.pkg.id &&
				rel.isDisplayed !== true &&
				rel.isIncludedInPackage !== false
		)

		// If there are no real rows, return nothing (avoid showing virtual/display rows).
		if (realRelationsForThisPackage.length === 0) {
			return []
		}

		return attachLineItems(realRelationsForThisPackage)
	}
)

const imageUrl = computedAsync(() =>
	asset.getImageBlobUrl({
		filename: props.pkg.productImageUrl,
		name: props.pkg.name,
	} as AssetStoryblok)
)
</script>
